import moment from 'moment';
import DateRangeFilter from 'components/Grid/Filter/DateRange';
import BooleanView from 'components/DataGrid/ValueViews/BooleanView';
import OrderStatusCell from 'components/Grid/Cells/OrderStatusCell';
import NumberRangeFilter from 'components/Grid/Filter/NumberRange';
import DeliveryPhoto from 'components/DeliveryPhoto/DeliveryPhoto';

import YesNoDataGridFilter from '../components/YesNoDataGridFilter';
import { translateOrderStatuses } from '../../Orders/Orders/statusTranslations';
import HistoryAddons from '../components/HistoryAddons';
import PriceAfterDiscountTooltip from '../components/PriceAfterDiscountTooltip';
import EcommerceDateRange from '../components/EcommerceDateRange';

const dateFormat = 'YYYY-MM-DD';

const columnConfig = t => [
  {
    title: 'ecommerceBags.fields.id',
    accessor: 'id',
    name: 'id',
    width: 70,
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.id',
    name: 'dietId',
    width: 70,
    filterable: true,
    sortable: true,
    accessor: row => (
      <a
        target="_blank"
        rel="noreferrer"
        href={`/admin/ecommerce-diets/preview/${row.dietId}`}
      >
        {row.dietId}
      </a>
    ),
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.order.id',
    accessor: 'orderId',
    name: 'orderId',
    width: 70,
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.isSubscription',
    accessor: row => <BooleanView value={row.isSubscription} />,
    name: 'isSubscription',
    width: 70,
    Filter: YesNoDataGridFilter,
    filterable: true,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.active',
    accessor: row => <BooleanView value={row.active} />,
    name: 'active',
    width: 70,
    Filter: YesNoDataGridFilter,
    filterable: true,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.order.status',
    accessor: row => (
      <OrderStatusCell
        status={{ value: row.status, systemValue: row.systemStatus }}
      />
    ),
    filterable: true,
    sortable: false,
    width: 170,
    name: 'systemStatus',
    Filter: ({ onChange, filter }) => {
      const selectOptions = translateOrderStatuses(t);

      return (
        <select
          style={{ height: '30px', width: '100%', padding: '0' }}
          onChange={e => onChange(e.target.value)}
          value={filter?.value || ''}
        >
          <option value="">{selectOptions[0].label}</option>
          <option value={selectOptions[1].systemValue}>
            {selectOptions[1].label}
          </option>
          <option value={selectOptions[2].systemValue}>
            {selectOptions[2].label}
          </option>
          <option value={selectOptions[3].systemValue}>
            {selectOptions[3].label}
          </option>
        </select>
      );
    },
  },
  {
    title: 'ecommerceBags.fields.bag.date',
    accessor: row => moment(row.date).format(dateFormat),
    name: 'date',
    Filter: ({ onChange, filter }) => {
      const validationCalendar = current => {
        const minDate = moment().subtract(1, 'years').startOf('day');
        const maxDate = moment().subtract(1, 'day').endOf('day');
        return current.isBetween(minDate, maxDate, 'day', '[]');
      };

      return (
        <EcommerceDateRange
          onChange={onChange}
          value={filter?.value}
          validationCalendar={validationCalendar}
        />
      );
    },

    width: 90,
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.deliveryTime',
    accessor: row =>
      row.deliveredAt ? moment(row.deliveredAt).format('llll') : null,
    name: 'deliveredAt',
    Filter: ({ onChange, filter }) => (
      <DateRangeFilter onChange={onChange} value={filter?.value} />
    ),
    filterable: true,
    sortable: false,
  },
  {
    title: t('ecommerceBags.fields.bag.deliveryPhoto'),
    accessor: row =>
      row.deliveryImage ? (
        <DeliveryPhoto src={row.deliveryImage} id={row.id} />
      ) : null,
    name: 'deliveryPhoto',
    filterable: false,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.client',
    accessor: row => (
      <a
        target="_blank"
        rel="noreferrer"
        href={`/admin/clients/edit/${row.clientId}`}
      >
        {row.clientFirstName} {row.clientLastName}
      </a>
    ),
    name: 'user',
    width: 200,
    filterable: true,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.client.email',
    accessor: prop => (
      <a href={`mailto:${prop.clientEmail}`}>{prop.clientEmail}</a>
    ),
    width: 200,
    name: 'clientEmail',
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.addons',
    accessor: row => <HistoryAddons addons={row.addons} />,
    width: 200,
    name: 'hasAddons',
    Filter: YesNoDataGridFilter,
    filterable: true,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.client.phone.number',
    accessor: prop => (
      <a href={`tel:${prop.clientPhoneNumber}`}>{prop.clientPhoneNumber}</a>
    ),
    width: 110,
    name: 'bag.clientPhoneNumber',
  },
  {
    title: 'ecommerceBags.fields.bag.address.city',
    accessor: 'city',
    name: 'city',
    width: 70,
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.address',
    accessor: row =>
      row.city
        ? `${row.city} ${row.postCode}, ${row.street} ${row.buildNumber}`
        : row.pickUpPoint || '-',
    width: 200,
    name: 'address',
    sortable: true,
    filterable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.address.zone.name',
    accessor: 'zoneName',
    name: 'zoneName',
    width: 70,
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.realFoodCost',
    accessor: 'realFoodCost',
    name: 'realFoodCost',
    filterable: true,
    sortable: true,
    Filter: ({ onChange, filter }) => (
      <NumberRangeFilter onFilterChange={onChange} value={filter?.value} />
    ),
  },
  {
    title: 'ecommerceBags.fields.bag.priceFromMoneyBox',
    accessor: row => row?.priceFromMoneyBox.toFixed(2) || '-',
    Filter: ({ onChange, filter }) => (
      <NumberRangeFilter onFilterChange={onChange} value={filter?.value} />
    ),
    name: 'priceFromMoneyBox',
    sortable: true,
    filterable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.priceAfterDiscount',
    name: 'priceAfterDiscount',
    accessor: ({
      ecoContainerPrice,
      priceAddonsAfterDiscount,
      priceAddonsBeforeDiscount,
      priceAddonsDiscount,
      priceAfterDiscount,
      priceBeforeDiscount,
      priceDeliveryAfterDiscount,
      priceDeliveryBeforeDiscount,
      priceDeliveryDiscount,
      priceDiscount,
      priceDishesAfterDiscount,
      priceDishesBeforeDiscount,
      priceDishesDiscount,
    }) => (
      <div style={{ textAlign: 'right', paddingRight: 8 }}>
        <PriceAfterDiscountTooltip
          ecoContainerPrice={ecoContainerPrice}
          priceAddonsAfterDiscount={priceAddonsAfterDiscount}
          priceAddonsBeforeDiscount={priceAddonsBeforeDiscount}
          priceAddonsDiscount={priceAddonsDiscount}
          priceAfterDiscount={priceAfterDiscount}
          priceBeforeDiscount={priceBeforeDiscount}
          priceDeliveryAfterDiscount={priceDeliveryAfterDiscount}
          priceDeliveryBeforeDiscount={priceDeliveryBeforeDiscount}
          priceDeliveryDiscount={priceDeliveryDiscount}
          priceDiscount={priceDiscount}
          priceDishesAfterDiscount={priceDishesAfterDiscount}
          priceDishesBeforeDiscount={priceDishesBeforeDiscount}
          priceDishesDiscount={priceDishesDiscount}
        />
      </div>
    ),
    Filter: ({ onChange, filter }) => (
      <NumberRangeFilter onFilterChange={onChange} value={filter?.value} />
    ),
    filterable: true,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.income',
    accessor: 'income',
    name: 'income',
    filterable: true,
    sortable: true,
    Filter: ({ onChange, filter }) => (
      <NumberRangeFilter onFilterChange={onChange} value={filter?.value} />
    ),
  },
  {
    title: 'ecommerceBags.fields.bag.diet.name',
    accessor: 'dietName',
    name: 'dietName',
    width: 70,
    filterable: true,
    sortable: true,
  },

  {
    title: 'ecommerceBags.fields.bag.clientDiet.diet.name',
    accessor: 'clientDietName',
    name: 'clientDietName',
    width: 70,
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.variant.name',
    accessor: 'variantName',
    name: 'variantName',
    width: 70,
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.variant.name',
    accessor: 'clientVariantName',
    name: 'clientVariantName',
    width: 70,
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.calorific.name',
    accessor: 'calorificName',
    name: 'calorificName',
    width: 70,
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.calorific.name',
    accessor: 'clientCalorificName',
    name: 'clientCalorificName',
    width: 70,
    filterable: true,
    sortable: true,
  },

  {
    title: 'ecommerceBags.fields.bag.optionChangeMenu',
    accessor: row => <BooleanView value={row.optionChangeMenu} />,
    name: 'optionChangeMenu',
    width: 70,
    Filter: YesNoDataGridFilter,
    filterable: true,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.optionChangeMenu',
    accessor: row => <BooleanView value={row.clientOptionChangeMenu} />,
    name: 'clientOptionChangeMenu',
    width: 70,
    Filter: YesNoDataGridFilter,
    filterable: true,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.useEcoContainers',
    accessor: row => <BooleanView value={row.useEcoContainers} />,
    name: 'useEcoContainers',
    width: 70,
    Filter: YesNoDataGridFilter,
    filterable: true,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.useEcoContainers',
    accessor: row => <BooleanView value={row.clientUseEcoContainers} />,
    name: 'clientUseEcoContainers',
    width: 70,
    Filter: YesNoDataGridFilter,
    filterable: true,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.clientChanged',
    accessor: row => <BooleanView value={row.clientChanged} />,
    name: 'clientChanged',
    width: 70,
    Filter: YesNoDataGridFilter,
    filterable: true,
    sortable: false,
  },
];
export default columnConfig;
